import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';

// ── Types ──
export interface SocialMediaPost {
  id: string;
  akte_id: string | null;
  kunde_id: string | null;
  titel: string;
  post_text: string;
  plattform: string;
  template_typ: string;
  status: string; // entwurf | bereit | geplant | veroeffentlicht
  fahrzeug_marke: string | null;
  fahrzeug_modell: string | null;
  schadenhoehe: number | null;
  wiederbeschaffungswert: number | null;
  schadenart: string | null;
  bild_urls: string[];
  kennzeichen_unkenntlich: boolean;
  instagram_einwilligung: boolean;
  geplant_fuer: string | null;
  veroeffentlicht_am: string | null;
  meta_post_id: string | null;
  hashtags: string[];
  erstellt_von: string | null;
  created_at: string;
  updated_at: string;
}

export interface PostTemplate {
  id: string;
  name: string;
  typ: string;
  beschreibung: string | null;
  text_vorlage: string;
  hashtag_vorlage: string[];
  layout_config: Record<string, any>;
  aktiv: boolean;
  created_at: string;
}

export interface KundeMarketing {
  id: string;
  vorname: string;
  nachname: string;
  email: string | null;
  instagram_einwilligung: boolean;
  instagram_handle: string | null;
  bewertung_angefragt: boolean;
  bewertung_bestaetigt: boolean;
  tankgutschein_vergeben: boolean;
  tankgutschein_datum: string | null;
}

export interface MarketingStats {
  postsGesamt: number;
  postsVeroeffentlicht: number;
  postsEntwurf: number;
  postsBereit: number;
  postsGeplant: number;
  kundenMitEinwilligung: number;
  bewertungenAngefragt: number;
  bewertungenBestaetigt: number;
  tankgutscheineVergeben: number;
}

// ── Hook: useMarketing ──
export function useMarketing() {
  const [posts, setPosts] = useState<SocialMediaPost[]>([]);
  const [templates, setTemplates] = useState<PostTemplate[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchPosts = useCallback(async () => {
    const { data, error } = await supabase
      .from('social_media_posts')
      .select('*')
      .order('created_at', { ascending: false });
    if (!error && data) setPosts(data);
    return data || [];
  }, []);

  const fetchTemplates = useCallback(async () => {
    const { data, error } = await supabase
      .from('social_media_templates')
      .select('*')
      .eq('aktiv', true)
      .order('name');
    if (!error && data) setTemplates(data);
    return data || [];
  }, []);

  const createPost = useCallback(async (post: Partial<SocialMediaPost>) => {
    const { data, error } = await supabase
      .from('social_media_posts')
      .insert(post)
      .select()
      .single();
    if (!error && data) {
      setPosts(prev => [data, ...prev]);
    }
    return { data, error };
  }, []);

  const updatePost = useCallback(async (id: string, updates: Partial<SocialMediaPost>) => {
    const { data, error } = await supabase
      .from('social_media_posts')
      .update({ ...updates, updated_at: new Date().toISOString() })
      .eq('id', id)
      .select()
      .single();
    if (!error && data) {
      setPosts(prev => prev.map(p => p.id === id ? data : p));
    }
    return { data, error };
  }, []);

  const deletePost = useCallback(async (id: string) => {
    const { error } = await supabase.from('social_media_posts').delete().eq('id', id);
    if (!error) setPosts(prev => prev.filter(p => p.id !== id));
    return { error };
  }, []);

  const movePost = useCallback(async (id: string, newStatus: string) => {
    const extra: Record<string, any> = {};
    if (newStatus === 'veroeffentlicht') extra.veroeffentlicht_am = new Date().toISOString();
    return updatePost(id, { status: newStatus, ...extra });
  }, [updatePost]);

  useEffect(() => {
    setLoading(true);
    Promise.all([fetchPosts(), fetchTemplates()]).finally(() => setLoading(false));
  }, [fetchPosts, fetchTemplates]);

  return { posts, templates, loading, fetchPosts, fetchTemplates, createPost, updatePost, deletePost, movePost };
}

// ── Hook: useMarketingStats ──
export function useMarketingStats() {
  const [stats, setStats] = useState<MarketingStats>({
    postsGesamt: 0, postsVeroeffentlicht: 0, postsEntwurf: 0, postsBereit: 0, postsGeplant: 0,
    kundenMitEinwilligung: 0, bewertungenAngefragt: 0, bewertungenBestaetigt: 0, tankgutscheineVergeben: 0,
  });
  const [loading, setLoading] = useState(true);

  const fetchStats = useCallback(async () => {
    setLoading(true);
    try {
      const [postsRes, kundenRes] = await Promise.all([
        supabase.from('social_media_posts').select('status'),
        supabase.from('kunden').select('instagram_einwilligung, bewertung_angefragt, bewertung_bestaetigt, tankgutschein_vergeben'),
      ]);
      const allPosts = postsRes.data || [];
      const allKunden = kundenRes.data || [];
      setStats({
        postsGesamt: allPosts.length,
        postsVeroeffentlicht: allPosts.filter(p => p.status === 'veroeffentlicht').length,
        postsEntwurf: allPosts.filter(p => p.status === 'entwurf').length,
        postsBereit: allPosts.filter(p => p.status === 'bereit').length,
        postsGeplant: allPosts.filter(p => p.status === 'geplant').length,
        kundenMitEinwilligung: allKunden.filter(k => k.instagram_einwilligung).length,
        bewertungenAngefragt: allKunden.filter(k => k.bewertung_angefragt).length,
        bewertungenBestaetigt: allKunden.filter(k => k.bewertung_bestaetigt).length,
        tankgutscheineVergeben: allKunden.filter(k => k.tankgutschein_vergeben).length,
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchStats(); }, [fetchStats]);

  return { stats, loading, refetch: fetchStats };
}

// ── Hook: useKundenMarketing ──
export function useKundenMarketing() {
  const [kunden, setKunden] = useState<KundeMarketing[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchKunden = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('kunden')
      .select('id, vorname, nachname, email, instagram_einwilligung, instagram_handle, bewertung_angefragt, bewertung_bestaetigt, tankgutschein_vergeben, tankgutschein_datum')
      .order('nachname');
    if (!error && data) setKunden(data as KundeMarketing[]);
    setLoading(false);
    return data || [];
  }, []);

  const updateKunde = useCallback(async (id: string, updates: Partial<KundeMarketing>) => {
    const { data, error } = await supabase.from('kunden').update(updates).eq('id', id).select().single();
    if (!error && data) setKunden(prev => prev.map(k => k.id === id ? { ...k, ...data } : k));
    return { data, error };
  }, []);

  useEffect(() => { fetchKunden(); }, [fetchKunden]);

  return { kunden, loading, fetchKunden, updateKunde };
}

// ── Helper: Post-Text aus Akte generieren ──
export function generatePostText(template: PostTemplate, akte: Record<string, any>): { text: string; hashtags: string[] } {
  let text = template.text_vorlage;
  text = text.replace(/\{\{fahrzeug_marke\}\}/g, akte.fahrzeug_marke || 'Fahrzeug');
  text = text.replace(/\{\{fahrzeug_modell\}\}/g, akte.fahrzeug_modell || '');
  text = text.replace(/\{\{wbw\}\}/g, akte.wiederbeschaffungswert ? Number(akte.wiederbeschaffungswert).toLocaleString('de-DE') : '—');
  text = text.replace(/\{\{schadenhoehe\}\}/g, akte.reparaturkosten_brutto ? Number(akte.reparaturkosten_brutto).toLocaleString('de-DE') : '—');
  text = text.replace(/\{\{tipp_titel\}\}/g, akte.tipp_titel || 'Wussten Sie schon?');
  text = text.replace(/\{\{tipp_text\}\}/g, akte.tipp_text || '');
  return { text, hashtags: [...template.hashtag_vorlage] };
}
